// 統一待辦事項計數 — 給 sidebar badge + WorkflowBell badge 共用。
// 2026-05-11 user spec: 「待辦事項那邊應該要有個數字告訴我有多少個待辦事項」
//
// 計算口徑 (2026-05-12 修正, 跟 MyInbox 顯示一致):
//   total = 待我處理 workflow + 我發起被退回 (V105)
//   pendingParties 仍 query (給 WorkflowBell 顯示分項), 但不計入 sidebar badge
//   — 因為 MyInbox 頁面不顯示 pending parties, 加進 total 會造成 badge=2 但
//   inbox 空的不一致。 admin 看 pending parties 走 /parties 列表 (amber row tint)。
//
// 多元件共享 — TanStack Query cache 自動 dedupe, 不會打多次 API。
// refetchInterval 30s 同 bell cadence。

import { useQuery } from '@tanstack/react-query';
import { useActor, useIsAdmin } from './actor-store.ts';
import { api } from './api.ts';
import { useApprovalsList } from './use-approvals.ts';
import { useIsHr, useIsSysadminCap } from './use-capabilities.ts';
import { usePendingMonthlyReports } from './use-monthly-report.ts';
import { usePayrollTodo } from './use-payroll.ts';
import { useAwaitingMe, useRejectedInitiatedByMe } from './use-workflows.ts';

export interface PendingPartyRow {
  id: string;
  code: string | null;
  name_zh: string | null;
  name_en: string | null;
  kyc_status: string;
  status: string;
}

export function usePendingParties(enabled: boolean) {
  const actor = useActor((s) => s.actor);
  return useQuery<PendingPartyRow[]>({
    queryKey: ['parties', 'pending-bell', actor],
    queryFn: () => api<PendingPartyRow[]>('/api/v1/parties?status=pending&limit=20', { actor }),
    enabled,
    // 2026-08-01 效能：30s → 60s，改用回到分頁即刷新（見 use-workflows.ts 說明）。
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

export interface TodoBreakdown {
  /** 待我處理 — workflow_instance assigned to me / 我能處理 */
  awaitingWorkflows: number;
  /** 夥伴待核可 — party.status='pending' (admin only; non-admin = 0) */
  pendingParties: number;
  /** V105: 我發起被退回的 workflow — 需我看退回理由 + 改 + 重送 OR 放棄 */
  rejectedByMe: number;
  /** 待我簽核 — approval_request inbox (報帳/請假/付款 輪到我簽) */
  approvalInbox: number;
  /**
   * 2026-08-24: 薪資的最後一哩 —— 該轉帳了 / 該通知員工了。
   * 只有人資 / 系統管理員拿得到 (端點 requireHrOrSysadmin, 其他人回 0)。
   * 轉帳那項在**實際發放日前 2 個工作天**出現 (遇六日自動提前, 使用者要求)。
   */
  payrollTodo: number;
  /** V484: 已凍結、還沒寄的月報 (管理員才有; 其他人 0)。 */
  monthlyReportTodo: number;
  /**
   * total = awaitingWorkflows + rejectedByMe + approvalInbox
   * 2026-05-12: pendingParties 不計入 — 跟 MyInbox 顯示對齊。 admin 看 pending
   * party 走 /parties 列表 (amber row tint), 不重複算進 sidebar badge。
   * 2026-07-07: approvalInbox 計入 — 我的待辦已把簽核待我簽併進清單, badge==inbox。
   * 2026-08-24: payrollTodo 計入 — 它是真的「要我去做的事」(轉帳 / 寄薪資條),
   * 不計入的話發薪日到了 badge 卻沒動, 等於沒提醒。
   * 2026-10-04: monthlyReportTodo 計入 —— 5 號提醒寄月報就是靠這一項 (系統不自己寄信)。
   */
  total: number;
}

/** 給 sidebar / bell 顯示的「我能做的待辦數」單一 source of truth. */
export function useTodoCount(): TodoBreakdown {
  const isAdmin = useIsAdmin();
  const isHr = useIsHr();
  const isSysadmin = useIsSysadminCap();
  const actor = useActor((s) => s.actor);
  const awaiting = useAwaitingMe();
  const parties = usePendingParties(isAdmin);
  const rejected = useRejectedInitiatedByMe();
  const approvals = useApprovalsList('inbox', actor);
  // 只有人資 / 系統管理員看得到薪資待辦 (端點會擋, 這裡也不必為別人打)。
  const payroll = usePayrollTodo(actor, isHr || isSysadmin);
  // V484: 月報待寄出 —— 只有管理員能寄, 也只有他們的待辦有這一項。
  const monthly = usePendingMonthlyReports(actor, isAdmin);
  const awaitingN = awaiting.data?.length ?? 0;
  const partiesN = isAdmin ? (parties.data?.length ?? 0) : 0;
  const rejectedN = rejected.data?.length ?? 0;
  const approvalsN = approvals.data?.length ?? 0;
  const payrollN = payroll.data?.items.length ?? 0;
  const monthlyN = isAdmin ? (monthly.data?.length ?? 0) : 0;
  return {
    awaitingWorkflows: awaitingN,
    pendingParties: partiesN,
    rejectedByMe: rejectedN,
    approvalInbox: approvalsN,
    payrollTodo: payrollN,
    monthlyReportTodo: monthlyN,
    // 2026-05-12: pendingParties 不算進 total (MyInbox 沒顯示, 算進去會 badge≠inbox)
    // 2026-07-07: approvalInbox 算進 total (MyInbox 已顯示簽核待我簽)
    total: awaitingN + rejectedN + approvalsN + payrollN + monthlyN,
  };
}
